import { Box, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useCallback } from "react"

import { ORDER_STATUS_LABEL_LOOKUP, SHIPPING_STATUS_LABEL_LOOKUP } from "../../constants"

import { PurchaseOrderFilterChip } from "./PurchaseOrderFilterChip"

import type { DateRange, OrderListFilter, OrderStatus, ShipStatus } from "@/graphql/codegen/graphql"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams"

export const PurchaseOrderSearchFilterChips = (): ReactNode => {
  const navigate = useNavigate({ from: "/app/procurement/_layout/purchase-orders" as any })
  const currentSearch = useSearch({ from: "/app/procurement/_layout/purchase-orders" as any })
  const { statuses = [], shipStatuses = [], start, end, outstandingTestResults } = currentSearch

  const handleRemoveFilter = useCallback(
    (type: keyof OrderListFilter | keyof DateRange, value: OrderStatus | ShipStatus | null) => {
      const updatedSearch = { ...currentSearch }

      switch (type) {
        case "start":
          updatedSearch.start = undefined
          updatedSearch.end = undefined
          break
        case "end":
          updatedSearch.end = undefined
          break
        case "outstandingTestResults":
          updatedSearch.outstandingTestResults = undefined
          break
        default:
          updatedSearch[type] = (currentSearch[type] ?? []).filter((status: any) => status !== value)
          break
      }

      const params = getRefinedSearchParams(currentSearch, updatedSearch)
      navigate(params as any)
    },
    [currentSearch, navigate]
  )

  if (!statuses?.length && !shipStatuses?.length && !start && !end && !outstandingTestResults) {
    return null
  }

  return (
    <Box data-testid='purchase-order-search-filter-chips' display='flex' flexWrap='wrap' alignItems='center' gap={2}>
      <Typography variant='body1'>Filters:</Typography>
      {statuses?.map((status: OrderStatus) => (
        <PurchaseOrderFilterChip
          key={`status-${status}`}
          label={`Status: ${ORDER_STATUS_LABEL_LOOKUP[status]}`}
          onDelete={() => handleRemoveFilter("statuses", status)}
        />
      ))}
      {shipStatuses?.map((status: ShipStatus) => (
        <PurchaseOrderFilterChip
          key={`shipStatus-${status}`}
          label={`Ship Status: ${SHIPPING_STATUS_LABEL_LOOKUP[status]}`}
          onDelete={() => handleRemoveFilter("shipStatuses", status)}
        />
      ))}
      {outstandingTestResults && (
        <PurchaseOrderFilterChip
          label='Outstanding Test Results'
          onDelete={() => handleRemoveFilter("outstandingTestResults", null)}
        />
      )}
      {start && (
        <PurchaseOrderFilterChip
          key='date-range'
          label={`PO Sent: ${new Date(start).toLocaleDateString()}${end ? ` - ${new Date(end).toLocaleDateString()}` : ""}`}
          onDelete={() => handleRemoveFilter("start", null)}
        />
      )}
    </Box>
  )
}
